import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { Footer, TitleBar } from '../../../../components';
import { useMutation, useQuery } from '@apollo/client';

import QueryErrorBoundary from '../../../../components/error-handling';
import { ColumnsType } from 'antd/es/table';
import { cleanUpObject, tableSortingMethods } from '../../../../utils';
import { baseColumns } from './export';
import { templateColumnConfigs } from './template';
import { capitalize } from 'lodash';
import {
  CREATE_HOSTING_LOCATION,
  DELETE_HOSTING_LOCATION,
  FETCH_HOSTING_LOCATIONS,
  UPDATE_HOSTING_LOCATION,
} from '../../queries/hostingLocations';
import { useForm } from 'react-hook-form';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { RedirectToTierListButton, useTiers } from '../../tiers';
import { TierBadge } from '../../tiers/components';
import { TierListOptions } from '../../tiers/types';
import { useMemo } from 'react';

export type HostingLocationsEntity = {
  address: string | null;
  businessOwnerEmail: string | null;
  businessOwnerName: string | null;
  drInPlace: boolean | null;
  tier: TierListOptions | null;
  hostingAssetOrServer: string | null;
  hostingEntityName: string | null;
  itOwnerEmail: string | null;
  itOwnerName: string | null;
  rebuildTime: string | null;
  type: string | null;
  uuid: string;
};

export type ITApplicationsEntity = {
  application: string | null;
  applicationType: string | null;
  description: string | null;
  drpDate: string | null;
  drpLink: string | null;
  hostingLocationsAsset: HostingLocationsEntity[] | null;
  hostingLocationsEntity: HostingLocationsEntity[] | null;
  lastDrpTest: string | null;
  mto: string | null;
  rpo: string | null;
  rto: string | null;
  sla: string | null;
  supportsEmployees: boolean | null;
  supportsLifeSafety: boolean | null;
  supportsOther: boolean | null;
  supportsRegulation: boolean | null;
  supportsReputation: boolean | null;
  uuid: string;
};

export type ITApplicationsAsset = {
  application: string | null;
  applicationType: string | null;
  description: string | null;
  drpDate: string | null;
  drpLink: string | null;
  lastDrpTest: string | null;
  mto: string | null;
  rpo: string | null;
  rto: string | null;
  sla: string | null;
  supportsEmployees: boolean | null;
  supportsLifeSafety: boolean | null;
  supportsOther: boolean | null;
  supportsRegulation: boolean | null;
  supportsReputation: boolean | null;
  uuid: string;
};

export type FindAllHostingLocations = {
  ITApllicationsEntity: ITApplicationsEntity[] | null;
  ITApplicationsAsset: ITApplicationsAsset[] | null;
  address: string | null;
  businessOwnerEmail: string | null;
  businessOwnerName: string | null;
  drInPlace: boolean | null;
  tier: {id: number; name: string; color: string} | null;
  hostingAssetOrServer: string | null;
  hostingEntityName: string | null;
  itOwnerEmail: string | null;
  itOwnerName: string | null;
  rebuildTime: string | null;
  type: string | null;
  uuid: string;
};

type DataReturned = {
  findAllHostingLocations: FindAllHostingLocations[];
};

const HOSTING_LOCATIONS_TYPES = {
  PHYSICAL: 'PHYSICAL',
  VIRTUAL: 'VIRTUAL',
  NETWORK: 'NETWORK',
  STORAGE: 'STORAGE',
};

const HostingLocations = () => {
  const formInstance = useForm();

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_HOSTING_LOCATIONS,
  );

  const { data: tiers } = useTiers();

  const [createITApplication] = useMutation(CREATE_HOSTING_LOCATION, {
    refetchQueries: ['FindAllHostingLocations'],
  });

  const [updateITApplication] = useMutation(UPDATE_HOSTING_LOCATION, {
    refetchQueries: ['FindAllHostingLocations'],
  });

  const [deleteITApplication] = useMutation(DELETE_HOSTING_LOCATION, {
    refetchQueries: ['FindAllHostingLocations'],
  });

  const columns: ColumnsType<any> = [
    {
      title: 'Hosting Entity Name',
      dataIndex: 'hostingEntityName',
      key: 'hostingEntityName',
      width: 200,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.hostingEntityName, b.hostingEntityName),
      render: (text) =>
        text ? (
          <span className="custom-table--column">{text}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Hosting Asset / Server',
      dataIndex: 'hostingAssetOrServer',
      key: 'hostingAssetOrServer',
      width: 200,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(
          a.hostingAssetOrServer,
          b.hostingAssetOrServer,
        ),
      render: (text) =>
        text ? (
          <span className="custom-table--column">{text}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      align: 'center',
      filters: [
        { text: 'Physical', value: 'Physical' },
        { text: 'Virtual', value: 'Virtual' },
        { text: 'Network', value: 'Network' },
        { text: 'Storage', value: 'Storage' },
      ],
      onFilter: (value, record) => record.type === value,
      render: (text) =>
        text ? (
          <span className="custom-table--column">{capitalize(text)}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'IT Owner',
      dataIndex: 'itOwner',
      key: 'itOwner',
      width: 250,
      align: 'left',
      render: (_, record) =>
        record.itOwnerEmail || record.itOwnerName ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>{record.itOwnerName}</span>
            <span>{record.itOwnerEmail}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Business Owner',
      dataIndex: 'businessOwner',
      key: 'businessOwner',
      width: 250,
      align: 'left',
      render: (_, record) =>
        record.businessOwnerEmail || record.businessOwnerName ? (
          <div className="custom-table--column text-sm flex flex-col">
            <span>{record.businessOwnerName}</span>
            <span>{record.businessOwnerEmail}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      width: 300,
      align: 'left',
      render: (text, record) =>
        (record.type === HOSTING_LOCATIONS_TYPES.PHYSICAL ||
          record.type === HOSTING_LOCATIONS_TYPES.STORAGE) &&
        text ? (
          <span className="custom-table--column">{text}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Applicable</span>
        ),
    },
    {
      title: 'Rebuild Time (months)',
      dataIndex: 'rebuildTime',
      key: 'rebuildTime',
      width: 200,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.rebuildTime, b.rebuildTime),
      render: (text) =>
        text ? (
          <span className="custom-table--column">{text}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      title: 'DR in Place',
      dataIndex: 'drInPlace',
      key: 'drInPlace',
      width: 150,
      align: 'center',
      render: (text) => (
        <span className="custom-table--column">{text ? 'Yes' : 'No'}</span>
      ),
    },
    {
      title: 'DR Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 250,
      align: 'center',
      render: (tier) =>
        tier ? (
          <TierBadge
            color={tier.color}
            name={tier.name}
          />
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const formattedData = useMemo(() => {
    if (data) {
      return data.findAllHostingLocations.map((item) => {
        const { tier, ...itemWithoutTier } = item;
        return {
          ...itemWithoutTier,
          tierId: tier?.id?.toString() || null,
          tier: tier, // Preserve the tier object for the column render function
        };
      });
    }
    return [];
  }, [data]);

  const hostingLocationsFormatter = (data: any) => {
    // tierId is string make it to a number
    const tierId = data.tierId ? parseInt(data.tierId, 10) : null;

    const customData = {
      ...data,
      tierId,
    };

    return cleanUpObject(customData);
  };

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    return {
      ...hostingLocationsFormatter(values),
    };
  };

  const fields = useMemo(() => [
    {
      key: 'hostingEntityName',
      label: 'Hosting Entity Name',
      isRequired: true,
      type: 'text',
    },
    {
      key: 'hostingAssetOrServer',
      label: 'Hosting Asset / Server',
      type: 'text',
    },
    {
      key: 'type',
      label: 'Type',
      isRequired: true,
      type: 'select',
      options: [
        { label: 'Physical', value: 'PHYSICAL' },
        { label: 'Virtual', value: 'VIRTUAL' },
        { label: 'Network', value: 'NETWORK' },
        { label: 'Storage', value: 'STORAGE' },
      ],
    },
    {
      key: 'address',
      label: 'Address',
      type: 'text',
      hidden:
        formInstance?.watch('type') !== HOSTING_LOCATIONS_TYPES.PHYSICAL &&
        formInstance?.watch('type') !== HOSTING_LOCATIONS_TYPES.STORAGE,
    },
    {
      key: 'itOwnerName',
      label: 'IT Owner Name',
      type: 'text',
    },
    {
      key: 'itOwnerEmail',
      label: 'IT Owner Email',
      type: 'text',
    },
    {
      key: 'businessOwnerName',
      label: 'Business Owner Name',
      type: 'text',
    },
    {
      key: 'businessOwnerEmail',
      label: 'Business Owner Email',
      type: 'text',
    },

    {
      key: 'rebuildTime',
      label: 'Rebuild Time (months)',
      type: 'number',
      decoratorWord: 'months',
      isRequired: true,
    },
    {
      key: 'drInPlace',
      label: 'DR in Place',
      type: 'boolean',
    },
    {
      key: 'tierId',
      label: 'DR Tier',
      type: 'tierSelect',
      options: tiers?.map((tier) => ({ label: tier.name, value: tier.id.toString(), color: tier.color })),
    },
  ], [tiers, formInstance]);


  return (
    <>
      <TitleUpdater title="Hosting Entities & Sub-Entities" />
      <TitleBar
        title="Data Inputs"
        subtitle="Hosting Entities & Sub-Entities"
        brief="Add or remove Hosting Entities & Sub-Entities below. This allows MEO to map and quantify exposure to the broader supply chain."
        rightContent={<RedirectToTierListButton />}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Hosting Entities & Sub-Entities"
          columns={columns}
          data={formattedData}
          loading={loading}
          editable
          formFields={fields}
          createGQL={createITApplication}
          updateGQL={updateITApplication}
          deleteGQL={deleteITApplication}
          modifyVariables={modifyVariables}
          templateColumns={templateColumnConfigs}
          exportExcelColumns={baseColumns}
          formInstance={formInstance}
        />
      </QueryErrorBoundary>
      <Footer backPath="/data-inputs" />
    </>
  );
};

export default HostingLocations;
